// --- API Types ---

import { IStateManagedEntity } from "../../helpers/state-helpers/state-action";
import { SubSchool } from "../school/sub-school";
import { Teacher } from "../teacher/teacher";
import { Day, StringToDay } from "./day.enum";

export interface ILesson extends IStateManagedEntity {
  lessonCode: number;
  dayCode: number;
  lessonIndex: number;
}

export type ScheduleLessonDto = {
  lessonId: string;
  name?: string;
  day: string;
  lessonCode: number;
  className: string;
  subSchoolId: string;
};

export type VacancyLessonDto = {
  vacancyLessonId: string;
  lessonId: string;
  vacancyLessonCode: number;
  vacancyDayCode: number;
  vacancyLessonIndex: number;
  name?: string;
  date: Date;
  subSchoolId: string;
  teacherApplications?: string[];
  teacherAccepted?: string;
  teacherDeclined?: string[];
};

export type CreateVacancyLesson = {
  lessonId: string;
  subSchoolId: string;
  name?: string;
  vacancyLessonIndex: number;
  vacancyLessonCode: number;
  vacancyDayCode: number;
  date: Date;
};

export type UpdateVacancyLesson = CreateVacancyLesson;

// --- Internal types ---

export class ScheduleLesson implements ILesson {
  constructor(
    public id: string,
    public dayCode: Day,
    public lessonCode: number,
    public className: string,
    public subSchool: SubSchool,
    public lessonIndex: number,
    public name?: string,
  ) {}

  static fromScheduleLessonDto(
    dto: ScheduleLessonDto,
    subSchool: SubSchool,
  ): ScheduleLesson {
    if (subSchool.subSchoolId !== dto.subSchoolId) {
      throw Error(
        `Subschool with id ${subSchool.subSchoolId} does not match subschool id on dto ${dto.subSchoolId}`,
      );
    }
    return new ScheduleLesson(
      dto.lessonId,
      StringToDay(dto.day),
      dto.lessonCode,
      dto.className,
      subSchool,
      StringToDay(dto.day),
      dto.name,
    );
  }
}

export class VacancyLesson implements ILesson {
  constructor(
    public id: string,
    public lessonId: string,
    public lessonCode: number,
    public dayCode: number,
    public lessonIndex: number,
    public date: Date,
    public subSchool: SubSchool,
    public className: string,
    public name?: string,
    public teacherApplications?: Teacher[],
    public teacherAccepted?: Teacher,
    public teacherDeclined?: Teacher[],
  ) {}

  static equals(lessonA: ILesson, lessonB: ILesson): boolean {
    return (
      lessonA.lessonIndex === lessonB.lessonIndex &&
      lessonA.lessonCode === lessonB.lessonCode
    );
  }

  static fromVacancyLessonDto(
    dto: VacancyLessonDto,
    teachers: Teacher[],
    subSchool: SubSchool,
    className: string,
  ): VacancyLesson {
    return new VacancyLesson(
      dto.vacancyLessonId,
      dto.lessonId,
      dto.vacancyLessonCode,
      dto.vacancyDayCode,
      dto.vacancyLessonIndex,
      new Date(dto.date),
      subSchool,
      className,
      dto.name,
      dto.teacherApplications
        ? (dto.teacherApplications
            .filter(
              // Filter out teacher applications that are already accepted
              (application) => !dto.teacherAccepted?.includes(application),
            )
            .map((id) => teachers.find((t) => id === t.id))
            .filter((t) => !!t) as Teacher[])
        : undefined,
      teachers.find((t) => dto.teacherAccepted === t.id),
      dto.teacherDeclined
        ? (dto.teacherDeclined
            .map((id) => teachers.find((t) => id === t.id))
            .filter((t) => !!t) as Teacher[])
        : undefined,
    );
  }

  static toCreateVacancyLesson(lesson: VacancyLesson): CreateVacancyLesson {
    return {
      date: lesson.date,
      lessonId: lesson.lessonId,
      subSchoolId: lesson.subSchool.subSchoolId,
      vacancyDayCode: lesson.dayCode,
      vacancyLessonCode: lesson.lessonCode,
      vacancyLessonIndex: lesson.lessonIndex,
      name: lesson.name,
    };
  }

  static toUpdateVacancyLesson(lesson: VacancyLesson): UpdateVacancyLesson {
    return {
      date: lesson.date,
      lessonId: lesson.lessonId,
      subSchoolId: lesson.subSchool.subSchoolId,
      vacancyDayCode: lesson.dayCode,
      vacancyLessonCode: lesson.lessonCode,
      vacancyLessonIndex: lesson.lessonIndex,
      name: lesson.name,
    };
  }
}

export type EmptyLesson = {
  date: Date;
} & ILesson & Partial<VacancyLesson>;

export type SchedulerLesson = (VacancyLesson | EmptyLesson) & {
  isActive: boolean;
  canActivate?: boolean;
};

export const isVacancyLesson = (lesson: SchedulerLesson): boolean => {
  return (lesson as VacancyLesson).lessonId != null;
};
